import React from "react";
import { Button, Tooltip } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import { registerDocument, signDocumentVerification } from "api/customers";
import { useGetClientById } from "hooks/api/useGetClientById";
import { DOCUMENT_TYPE_ATTESTATIONS } from "@constants/documentTypes";
import { useGlobalToast } from "components/GlobalToastProvider";
import SignDocumentDialog from "./SignDocumentDialog";
import { useDocumentRegistrationState } from "./DocumentRegistrationProvider";

function signErrorMessage(error: unknown) {
  const response =
    typeof error === "object" && error !== null && "response" in error ? error.response : undefined;
  const status =
    typeof response === "object" && response !== null && "status" in response
      ? response.status
      : undefined;

  if (status === 403) {
    return "You can't sign this document right now — it may not be your turn or it was already signed.";
  }
  if (status === 400) {
    return "This document is already fully verified.";
  }
  return "Whoops! Something went wrong";
}

export default function DocumentSigningActions({ canSign }: { canSign: boolean }) {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const suffix = query.get("suffix");
  const registerType = query.get("type");
  const documentTitle = query.get("title") || "Document";
  const clientId = query.get("clientId");
  const showBack = query.get("showBack") === "true";
  const history = useHistory();
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();
  const { data: clientData, isLoading: isClientLoading } = useGetClientById(clientId);
  const {
    status,
    dialogOpen,
    openDialog,
    closeDialog,
    startRegistration,
    completeRegistration,
    failRegistration,
  } = useDocumentRegistrationState();

  const clientName = clientData?.data
    ? `${clientData.data.name || ""} ${clientData.data.child_last_name || ""}`.trim()
    : "";
  const dateGenerated = new Date().toLocaleDateString();
  const attestations = registerType ? DOCUMENT_TYPE_ATTESTATIONS[registerType] || [] : [];
  const disabled = status !== "idle" || isClientLoading;

  async function saveDocument(signatureDataUrl?: string) {
    if (!startRegistration()) return;
    const signing = signatureDataUrl !== undefined;

    try {
      const { document } = await registerDocument({
        suffix,
        type: registerType,
        client_id: Number(clientId),
        display_filename: `${documentTitle} – ${clientName} – ${dateGenerated}.pdf`,
        verified: false,
      });
      if (signing) {
        await signDocumentVerification(document.id, { signatureDataUrl });
      }
      queryClient.invalidateQueries(["clientDocuments"]);
      if (signing && clientId) {
        // The clinician's signature clears any override server-side.
        queryClient.removeQueries(["documentChecklistStatus", clientId]);
      }
      completeRegistration();
      showToast({ message: signing ? "Document Signed & Saved" : "Document Saved" });
      if (showBack) history.goBack();
    } catch (error) {
      failRegistration();
      console.error(signing ? "Failed to sign document:" : "Failed to register document:", error);
      showToast({
        message: signing ? signErrorMessage(error) : "Whoops! Something went wrong",
        errorState: true,
      });
    } finally {
      if (signing) closeDialog();
    }
  }

  return (
    <>
      <Tooltip title={!canSign ? "You do not have permission to sign generated documents" : false}>
        <span>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            disabled={disabled || !canSign}
            onClick={openDialog}
          >
            Sign & Verify
          </Button>
        </span>
      </Tooltip>
      <Button
        variant="contained"
        color="secondary"
        size="medium"
        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        disabled={disabled}
        onClick={() => saveDocument()}
      >
        Save as Unverified
      </Button>
      <SignDocumentDialog
        documentTitle={documentTitle}
        clientName={clientName}
        dateGenerated={dateGenerated}
        attestations={attestations}
        open={dialogOpen}
        onClose={closeDialog}
        onSign={({ dataUrl }) => saveDocument(dataUrl)}
      />
    </>
  );
}
